import { Head } from '@inertiajs/react'
import picchatCreate from '@/assets/create/picchat-create.avif'

const ROOT_URL = 'https://picchat.ai'
const DEFAULT_OG_SITE_NAME = 'picchat.ai'
const DEFAULT_TWITTER_CARD = 'summary'
const DEFAULT_TWITTER_SITE = '@nyaggah'
const DEFAULT_TWITTER_CREATOR = '@nyaggah'

// Construct full URL for the default image
const defaultImageUrl = `${ROOT_URL}${picchatCreate}`

const DEFAULT_TITLE = 'Your new workflow for image creation'
const DEFAULT_DESCRIPTION =
  'Produce stunning portraits, product shots, thumbnails, and creative visuals powered by cutting-edge AI and powerful style recipes.'

interface MetaTagsProps {
  title?: string
  description?: string
  og_title?: string
  og_type?: string
  og_url?: string
  og_image?: string
  og_description?: string
  og_site_name?: string
  twitter_card?: string
  twitter_title?: string
  twitter_site?: string
  twitter_creator?: string
  twitter_url?: string
  twitter_image?: string
  twitter_description?: string
}

export function MetaTags({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  og_title,
  og_type,
  og_url,
  og_image,
  og_description,
  og_site_name = DEFAULT_OG_SITE_NAME,
  twitter_card = DEFAULT_TWITTER_CARD,
  twitter_title,
  twitter_site = DEFAULT_TWITTER_SITE,
  twitter_creator = DEFAULT_TWITTER_CREATOR,
  twitter_url,
  twitter_image,
  twitter_description,
}: MetaTagsProps) {
  // Use provided values or defaults for images
  // If og_image is provided, use it; otherwise use default (for PersistentLayout defaults)
  const finalOgImage = og_image !== undefined ? og_image : defaultImageUrl
  const finalTwitterImage =
    twitter_image !== undefined ? twitter_image : defaultImageUrl
  const finalTwitterUrl = twitter_url || og_url

  // Prefix descriptions with "picchat.ai - " when provided
  const finalOgDescription = og_description
    ? `picchat.ai - ${og_description}`
    : undefined
  const finalTwitterDescription = twitter_description
    ? `picchat.ai - ${twitter_description}`
    : undefined

  // Use og_title for twitter_title if not provided
  const finalTwitterTitle = twitter_title || og_title

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      {og_title && <meta property="og:title" content={og_title} />}
      {og_type && <meta property="og:type" content={og_type} />}
      {og_url && <meta property="og:url" content={og_url} />}
      {/* Always render og:image - either provided or default */}
      <meta property="og:image" content={finalOgImage} />
      {finalOgDescription && (
        <meta property="og:description" content={finalOgDescription} />
      )}
      <meta property="og:site_name" content={og_site_name} />
      <meta name="twitter:card" content={twitter_card} />
      {finalTwitterTitle && (
        <meta name="twitter:title" content={finalTwitterTitle} />
      )}
      <meta name="twitter:site" content={twitter_site} />
      <meta name="twitter:creator" content={twitter_creator} />
      {finalTwitterUrl && <meta name="twitter:url" content={finalTwitterUrl} />}
      {/* Always render twitter:image - either provided or default */}
      <meta name="twitter:image" content={finalTwitterImage} />
      {finalTwitterDescription && (
        <meta name="twitter:description" content={finalTwitterDescription} />
      )}
    </Head>
  )
}
